import React from 'react';

import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';

import useUserHook from 'hooks/useUserHook';

import { PageNotFound } from 'components/common/ErrorPages/pageNotFound';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import { SideMenuListProps } from 'components/common/SideMenu/SideMenu';

import { Responsive } from 'components/Dashboards/responsive';
import { settingsConfig } from 'components/Settings/settingsConfig';
import AddRolePage from 'components/pages/AddRolePage';
import { EditProfilePage } from 'components/pages/EditProfilePage';
import { MyProfilePage } from 'components/pages/MyProfilePage';
import { NarrativeTemplatesPage } from 'components/pages/NarrativeTemplatesPage';
import { NewProfilePage } from 'components/pages/NewProfilePage';
import RolesManagementPage from 'components/pages/RolesManagementPage';
import { TagsCreatePage } from 'components/pages/TagsCreatePage';
import { TagsEditPage } from 'components/pages/TagsEditPage';
import { TagsPage } from 'components/pages/TagsPage';
import { UserGroupsCreatePage } from 'components/pages/UserGroupsCreatePage';
import { UserGroupsEditPage } from 'components/pages/UserGroupsEditPage';
import { UserGroupsPage } from 'components/pages/UserGroupsPage';
import { UsersManagementPage } from 'components/pages/UsersManagementPage';

import GuardedRoute from '../common/GuardedRoute/guardedRoute';
import NoPermission from './Components/Common/NoPermission/noPermission';

const sideMenuList: SideMenuListProps[] = [
    {
        ...settingsConfig.userProfile,
        id: 'accountAdministrationSettingsNavBar_UserProfile',
        muiIcon: (
            <MuiIcon icon={'manage_accounts'} type={'outlined'} fontSize={'2.5rem'} color={'var(--text-primary)'} />
        ),
    },
    {
        ...settingsConfig.userManagement,
        id: 'accountAdministrationSettingsNavBar_UsersManagement',
        muiIcon: <MuiIcon icon={'people_alt'} type={'outlined'} fontSize={'2.5rem'} color={'var(--text-primary)'} />,
        permissions: 'UserManagement',
    },
    {
        ...settingsConfig.userGroups,
        id: 'accountAdministrationSettingsNavBar_UserGroups',
        muiIcon: <MuiIcon icon={'groups'} fontSize={'2.5rem'} color={'var(--text-primary)'} />,
        permissions: 'UserManagement',
    },
    {
        ...settingsConfig.roleManagement,
        id: 'accountAdministrationSettingsNavBar_RolesManagement',
        muiIcon: <MuiIcon icon={'local_police'} type={'outlined'} fontSize={'2.5rem'} color={'var(--text-primary)'} />,
        permissions: 'RolesManagement',
    },
    {
        ...settingsConfig.tags,
        id: 'accountAdministrationSettingsNavBar_Tags',
        muiIcon: <MuiIcon icon={'local_offer'} fontSize={'2.5rem'} color={'var(--text-primary)'} />,
    },
    {
        ...settingsConfig.narrativeTemplates,
        id: 'accountAdministrationSettingsNavBar_NarrativeTemplates',
        muiIcon: <MuiIcon icon={'rtt'} fontSize={'2.5rem'} color={'var(--text-primary)'} />,
        permissions: 'EditNarrativeTemplate',
    },
];

const Settings = () => {
    const { path } = useRouteMatch();
    const { ability, isSwitchUser } = useUserHook();
    const UserManagement = ability.can('view', 'UserManagement');
    const ManageProjects = ability.can('view', 'ManageProjects');
    const RolesManagement = ability.can('view', 'RolesManagement');
    const EditNarrativeTemplate = ability.can('view', 'EditNarrativeTemplate');
    return (
        <Responsive sideMenu={sideMenuList} title={'Settings'}>
            <Switch>
                <Route exact path={`${path}/${settingsConfig.userProfile.link}`} component={MyProfilePage} />
                <GuardedRoute
                    path={`${path}/${settingsConfig.userManagement.link}/${settingsConfig.userManagementNewUser.link}`}
                    component={NewProfilePage}
                    auth={UserManagement}
                    redirect={`${path}/no-permission`}
                />
                <GuardedRoute
                    path={`${path}/${settingsConfig.userManagement.link}/${settingsConfig.userManagementEditUser.link}/:email`}
                    component={EditProfilePage}
                    auth={UserManagement || isSwitchUser}
                    redirect={`${path}/no-permission`}
                />
                <GuardedRoute
                    path={`${path}/${settingsConfig.userManagement.link}`}
                    component={UsersManagementPage}
                    auth={UserManagement}
                    redirect={`${path}/no-permission`}
                />
                <GuardedRoute
                    path={`${path}/${settingsConfig.userGroups.link}/${settingsConfig.userGroupsNew.link}`}
                    component={UserGroupsCreatePage}
                    auth={UserManagement}
                    redirect={`${path}/no-permission`}
                />
                <GuardedRoute
                    path={`${path}/${settingsConfig.userGroups.link}/${settingsConfig.userGroupsEdit.link}/:userGroupId`}
                    component={UserGroupsEditPage}
                    auth={UserManagement}
                    redirect={`${path}/no-permission`}
                />
                <GuardedRoute
                    path={`${path}/${settingsConfig.userGroups.link}`}
                    component={UserGroupsPage}
                    auth={UserManagement}
                    redirect={`${path}/no-permission`}
                />
                <GuardedRoute
                    path={[
                        `${path}/${settingsConfig.roleManagement.link}/${settingsConfig.roleManagementNewRole.link}`,
                        `${path}/${settingsConfig.roleManagement.link}/${settingsConfig.roleManagementEditRole.link}/:roleId`,
                    ]}
                    component={AddRolePage}
                    auth={RolesManagement}
                    redirect={`${path}/no-permission`}
                />
                <GuardedRoute
                    path={`${path}/${settingsConfig.roleManagement.link}`}
                    component={RolesManagementPage}
                    auth={RolesManagement}
                    redirect={`${path}/no-permission`}
                />
                <GuardedRoute
                    path={`${path}/${settingsConfig.tags.link}/${settingsConfig.tagsNew.link}`}
                    component={TagsCreatePage}
                    auth={ManageProjects}
                    redirect={`${path}/no-permission`}
                />
                <GuardedRoute
                    path={`${path}/${settingsConfig.tags.link}/${settingsConfig.tagsEdit.link}/:tagId`}
                    component={TagsEditPage}
                    auth={ManageProjects}
                    redirect={`${path}/no-permission`}
                />
                <GuardedRoute
                    path={`${path}/${settingsConfig.tags.link}`}
                    component={TagsPage}
                    auth={ManageProjects}
                    redirect={`${path}/no-permission`}
                />
                <GuardedRoute
                    path={`${path}/${settingsConfig.narrativeTemplates.link}`}
                    component={NarrativeTemplatesPage}
                    auth={EditNarrativeTemplate}
                    redirect={`${path}/no-permission`}
                />
                <Route exact path={`${path}/no-permission`} component={NoPermission} />
                <Route exact path={`${path}`}>
                    <Redirect to={`${path}/${settingsConfig.userProfile.link}`} />
                </Route>
                <Route path={'*'} component={PageNotFound} />
            </Switch>
        </Responsive>
    );
};
export default Settings;
